import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getToken } from '../auth/tokenStore';
import { getClientActivity, getApprovalQueue, type ClientActivityItem, type ApprovalQueueItem } from '../auth/api';
import { transitionMarketingPost } from '../api/transactions';
import { listLeads, getLeadForecast } from '../api/leads';
import { getToday } from '../api/today';
// R-1 (Daniel 2026-08-10): pending volume, expected closings, weekly funnel.
import { getPipelineReport } from '../api/home';
import { listTransactions } from '../api/transactions';
import { listTeamTasks } from '../api/tasks';
import { getFollowupsCount } from '../api/coordinator';
import { ResultSheet } from '../components/ConfirmSheet';
import { Icon, type IconName } from '../components/Icon';
import Layout, { Avatar, ModernButton } from '../components/Layout';
import ThisWeekCard from '../components/ThisWeekCard';
import WeeklyCloseoutBanner from '../components/WeeklyCloseoutBanner';
import PushNotificationsCard from '../components/PushNotificationsCard';
import RecentActivityCard from '../components/RecentActivityCard';
import DealDeadlinesCard from '../components/DealDeadlinesCard';
import KeyDatesStrip from '../components/KeyDatesStrip';
import DailyBriefingCard from '../components/DailyBriefingCard';
import SphereGrowthCard from '../components/SphereGrowthCard';
import PowerScoreCard from '../components/PowerScoreCard';
// Build 10 (Daniel 2026-09-08): this month vs last month, with pace.
import MonthCompareCard from '../components/MonthCompareCard';
import { formatRelative } from '../utils/dates';
import { downloadTransactionsCsv, formatAddress, getPortalStats, statusColor, statusLabel } from '../api/transactions';
import { formatMoney } from '../utils/money';

/**
 * Dashboard — modern shell, real-data stats + recent activity.
 */

interface HealthResponse {
  status: 'ok' | 'degraded';
  environment: 'development' | 'production';
  database: 'ok' | 'error';
  table_count: number;
  fingerprint: number;
  duration_ms: number;
  timestamp: string;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

async function fetchHealth(): Promise<HealthResponse> {
  const res = await fetch(`${API_BASE}/api/health`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function TodayBanner() {
  const q = useQuery({ queryKey: ['today', 'me'], queryFn: () => getToday('me'), staleTime: 60_000 });
  const total = q.data?.total;
  const top = q.data?.sections[0];
  return (
    <Link
      to="/today"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        padding: '12px 16px', marginBottom: 16, borderRadius: 'var(--radius-lg)', textDecoration: 'none',
        background: 'var(--brand-primary-soft, var(--bg-surface-2))', border: '1px solid var(--brand-primary, var(--border))',
        color: 'var(--text-heading)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          {total == null ? 'Today' : total === 0 ? 'Nobody is waiting on you today' : `${total} ${total === 1 ? 'person' : 'people'} to reach today`}
        </div>
        {top && top.items[0] && (
          <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 2, overflowWrap: 'anywhere' }}>
            First up: {top.items[0].name}. {top.items[0].why}
          </div>
        )}
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>Open Today →</span>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  // Read-only portals (lender + client) get a stripped home view.
  if (user?.portal === 'partner' || user?.portal === 'client') return <PartnerHome />;

  const { data: health } = useQuery({ queryKey: ['health'], queryFn: fetchHealth });

  const { data: leadsData } = useQuery({ queryKey: ['leads', 'all', 'all'], queryFn: () => listLeads() });
  const { data: txData } = useQuery({ queryKey: ['transactions'], queryFn: () => listTransactions() });
  const { data: myTasksData } = useQuery({
    queryKey: ['team-tasks', 'me', ''],
    queryFn: () => listTeamTasks({ assignee: 'me' }),
  });
  // Phase 3 (2026-07-05): pending post-close referral drafts (day 7/30/
  // 90/180/365) waiting for review on TC Today.
  const { data: followupsData } = useQuery({
    queryKey: ['tc-followups-count'],
    queryFn: getFollowupsCount,
  });

  const leadsCount = leadsData?.leads.filter((l) => l.stage !== 'closed' && l.stage !== 'lost').length ?? 0;
  const activeTx = txData?.transactions.filter((t) => t.status === 'active' || t.status === 'under_contract').length ?? 0;
  const myOpenTasks = myTasksData?.tasks.filter((t) => t.status !== 'completed' && t.status !== 'cancelled').length ?? 0;
  const overdueTasks =
    myTasksData?.tasks.filter(
      (t) => t.status !== 'completed' && t.due_date !== null && t.due_date * 1000 < Date.now(),
    ).length ?? 0;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <Layout>
      {/* Daniel 2026-07-20: Monday 12 Week Year close-out prompt. Self-
          hides unless last week's check-in is unsubmitted and it's Mon/Tue. */}
      <WeeklyCloseoutBanner />

      {/* M-21 (Daniel 2026-08-04): push enable prompt. Lives on Home
          because /team redirects agents away and their phones are the
          whole point. Compact mode self-hides once push is on (or the
          browser cannot do push), so it never squats on the dashboard. */}
      <PushNotificationsCard compact />

      {/* Build 64: the day's list, one line, one tap. Home stays the
          scoreboard; Today is where the calls get made. */}
      <TodayBanner />

      {/* Page header — greeting + actions.
          Mobile (2026-07-27): the row wraps and the h1 scales down with
          clamp() so a long first name can't push the action buttons off
          the right edge of a 390px phone. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 28,
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 240px' }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>{today}</div>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(24px, 6vw, 32px)',
              fontWeight: 700,
              color: 'var(--text-heading)',
              margin: 0,
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              overflowWrap: 'anywhere',
            }}
          >
            {greeting}, {user?.first_name ?? 'there'}.
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/leads/new" style={ModernButton.primary}>
            <Icon name="plus" size={14} />
            New lead
          </Link>
          <Link to="/submit" style={ModernButton.secondary}>
            <Icon name="sparkles" size={14} />
            Submit client
          </Link>
          {/* L-103b (Daniel 2026-07-15): quick-tile shortcut for
              Submit an Offer. Agents live in the offer form when a
              new buyer contract is being drafted, so a Home-page
              button saves the extra hop through /submit. */}
          <Link to="/submit/buyer-offer" style={ModernButton.secondary}>
            <Icon name="mail" size={14} />
            Submit offer
          </Link>
        </div>
      </div>

      {/* ORDER (Daniel 2026-07-27): numbers, then what's on fire, then the
          briefing. Previously the briefing was first and consumed the whole
          first screen — on a phone that's three scrolls of prose before you
          reach a single number or a single thing you can act on. A dashboard
          should answer "what needs me" before it explains anything. */}

      {/* M-8 (Daniel 2026-07-30): new-caller alert. A lead auto-created
          from an unknown inbound call is the hottest thing in the CRM —
          it gets a red banner at the very top of Home until someone
          works it (any stage change or conversion clears it). */}
      {(() => {
        const cutoff = Math.floor(Date.now() / 1000) - 48 * 3600;
        const hotCallers = (leadsData?.leads ?? []).filter(
          (l) => l.source === 'inbound_call' && l.stage === 'new' && l.created_at >= cutoff,
        );
        if (hotCallers.length === 0) return null;
        return (
          <div
            style={{
              background: '#FDE7E7',
              border: '1px solid #E5B7B7',
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#A32D2D', marginBottom: 6 }}>
              📞 {hotCallers.length === 1 ? 'A new number called' : `${hotCallers.length} new numbers called`} — call back while it's hot
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {hotCallers.slice(0, 5).map((l) => (
                <Link
                  key={l.id}
                  to={`/leads/${l.id}`}
                  style={{ fontSize: 13, color: '#A32D2D', textDecoration: 'underline', fontWeight: 500 }}
                >
                  {[l.first_name, l.last_name].filter(Boolean).join(' ') || `Lead #${l.id}`}
                  {' '}→
                </Link>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 28 }}>
        <StatCard label="Open leads" value={leadsCount} icon="users" to="/leads" hint="in the pipeline" accent="brand" />
        <StatCard label="Active transactions" value={activeTx} icon="briefcase" to="/transactions" hint="active or under contract" accent="success" />
        <StatCard
          label="My open tasks"
          value={myOpenTasks}
          icon="list-checks"
          to="/tasks"
          hint={`${overdueTasks} overdue`}
          accent={overdueTasks > 0 ? 'danger' : 'info'}
        />
        {/* Phase 3: post-close follow-ups waiting for review. Hidden at
            zero so the row isn't padded for teams with no recent closes. */}
        {(followupsData?.count ?? 0) > 0 && (
          <StatCard
            label="Follow-ups waiting"
            value={followupsData!.count}
            icon="mail"
            to="/tc-today"
            hint="post-close touches to review"
            accent="brand"
          />
        )}
        {/* Round L-7 (Daniel 2026-06-09) — forecasted pipeline dollars
            from open leads × stage probability. Hidden when zero so
            the stat row isn't padded with a meaningless "—" for teams
            that haven't filled in pre-approval amounts yet. */}
        <ForecastStatCard />
      </div>

      {/* R-1 (Daniel 2026-08-10): the money row. The stat row above counts
          things. The team asked to see what those things are worth:
          pending volume and how much is scheduled to close this week and
          this month, with next month on the line underneath so the owner
          can see past the end of the current month. */}
      <PipelineMoneyRow />

      {/* Build 10 (Daniel 2026-09-08): "have a this month vs last month as
          well so we can see how things are stacking up at this time." Last
          month through the same day by default, full month and same month
          last year on the toggles, pace line underneath. */}
      <MonthCompareCard />

      {/* R-1 (Daniel 2026-08-10): "can we get a total leads for the week.
          any conversion on those." Sits right under the money row because
          it is the front of the same pipeline. */}
      <WeeklyLeadFunnelCard />

      {/* R-1 (Daniel 2026-08-10): pending volume per person. Owner and
          operations only, enforced on the server as well. See
          canSeePerAgentMoney in apps/api/src/routes/home.ts. */}
      <PendingByAgentCard />

      {/* L-108 (Daniel 2026-07-21): compact "key dates, next 30 days"
          strip — close/inspection/appraisal/financing dates landing
          soon, grouped by type. Distinct from the per-deal Deal
          Deadlines table below; this is the quick scan up top. Self-
          hides on light days so it never pads the dashboard. */}
      <KeyDatesStrip />

      {/* Marketing posts waiting on the caller's approval — server
          filters to only what they can act on (auctions: ops; listings/
          buyer: primary agent or ops). Returns nothing for users who
          never have anything to approve. */}
      <ApprovalQueueCard />

      {/* Per-user AI daily briefing (2026-07-21) — the first thing on the
          dashboard. Role-derived focus decides the content; self-hides for
          focus 'none' or when there is nothing to say. */}
      <DailyBriefingCard />

      {/* Round L-11 (Daniel 2026-06-09) — Pipeline Power Score card.
          Sits above the rest of Home so it's the first thing the agent
          sees when checking in. Auto-derives most activities; only
          11ish manual items require one-tap logging. */}
      <div style={{ marginBottom: 28 }}>
        <PowerScoreCard />
      </div>

      {/* SystemCard (DB latency, table count, env) used to occupy a third
          of this row. That's developer telemetry on a realtor's home page —
          moved out. Recent leads now takes the full width. */}
      <div style={{ marginBottom: 16 }}>
        <RecentLeadsCard leads={leadsData?.leads ?? []} />
      </div>

      {/* Round J-2: one-glance widget for the start of the day —
          DTD2 call list + open tasks + appointments this week. */}
      <ThisWeekCard />

      {/* L-102 (Daniel 2026-07-08): recent activity digest. Team-wide
          counts of emails sent, texts sent, tasks completed, new
          leads, deals touched — for the last 24 hours and last 7 days.
          Quick "did we do anything?" pulse on Monday. */}
      <RecentActivityCard />

      {/* L-71 (Daniel 2026-07-04): Deal Deadlines — every active deal
          with contract/EM/inspection/appraisal/financing/close dates
          + commission %. Red = past+still open, amber = <=3 days out.
          Works for buyer, listing, and auction alike. */}
      <DealDeadlinesCard />

      {/* L-76 (Daniel 2026-07-04): per-agent sphere growth this month.
          Turns nurture from vague habit into an accountability KPI.
          Zero-count agents still render so inactivity is visible. */}
      <SphereGrowthCard />


      {/* Re-engagement signals — buyers/sellers/lenders who've been on the
          portal recently. If a client's been viewing their deal, that's
          usually a good moment to give them a call. */}
      <ClientActivityCard />

      <footer
        style={{
          marginTop: 48,
          paddingTop: 20,
          borderTop: '1px solid var(--border)',
          fontSize: 12,
          color: 'var(--text-muted)',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 6,
        }}
      >
        <span>Transaction Rocket · TurnKey team</span>
        <span>{health ? `Last sync ${formatRelative(Math.floor(new Date(health.timestamp).getTime() / 1000))}` : ''}</span>
      </footer>
    </Layout>
  );
}

const ACCENTS = {
  brand: { bg: '#EFEDFB', fg: '#5B55B3' },
  success: { bg: '#DCF1E5', fg: '#196B47' },
  info: { bg: '#DDEBED', fg: '#205A60' },
  warning: { bg: '#FBEACB', fg: '#7B5A1E' },
  danger: { bg: '#F8DCDC', fg: '#7C2A2A' },
};

/**
 * Potential commissions stat card (R-2, Daniel 2026-08-31).
 *
 * What the COMPANY keeps across the whole open book: for ACTIVE and PENDING
 * deals it is the summed per-deal house_commission (gross commission, minus
 * co-op and referrals, minus the agent's split) computed server-side by the
 * same waterfall the per-deal Commission card uses, so each deal's own split
 * is honored (Luis 60/40 contributes his 40, not a flat rate). The lead piece
 * is an estimate: the probability-weighted lead forecast x the team's one-side
 * commission rate x the company's default share, since leads have no split
 * yet. This replaced the gross-volume "Forecast pipeline" number. Active and
 * pending come from the pipeline-report endpoint (same money scope as the
 * Pending volume card); the weighted-lead piece stays on the leads forecast.
 */
function compactMoney(cents: number): string {
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`;
  if (d >= 1_000) return `$${Math.round(d / 1_000)}K`;
  return `$${Math.round(d)}`;
}

function ForecastStatCard() {
  const { data: forecast } = useQuery({
    queryKey: ['lead-forecast'],
    queryFn: () => getLeadForecast(),
    // 90s stale, same as the pipeline report. Fresh enough between /leads
    // and /home without asking for second-to-second accuracy.
    staleTime: 90_000,
  });
  const { data: pipeline } = usePipelineReport();

  const rate = pipeline?.commission_rate ?? 0.03;
  const companyShare = pipeline?.company_share ?? 0.4;
  // Active and pending are the real per-deal company net (house_commission)
  // summed on the server. Leads have no split yet, so estimate their company
  // net from the weighted lead forecast x rate x company share.
  const activeHouse = pipeline?.potential_commissions.active_cents ?? 0;
  const pendingHouse = pipeline?.potential_commissions.pending_cents ?? 0;
  const leadHouse = (forecast?.forecast_cents ?? 0) * rate * companyShare;
  const totalHouse = activeHouse + pendingHouse + leadHouse;
  if (totalHouse <= 0) return null;

  const scopeNote = pipeline?.money_scope === 'own' ? 'your deals' : 'team';
  const parts = [
    `active ${compactMoney(activeHouse)}`,
    `pending ${compactMoney(pendingHouse)}`,
    leadHouse > 0 ? `leads ${compactMoney(leadHouse)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <StatCard
      label="Potential commissions"
      value={`$${Math.round(totalHouse / 100).toLocaleString()}`}
      icon="trending-up"
      to="/transactions"
      hint={`net to company · ${parts} · ${scopeNote}`}
      accent="success"
    />
  );
}

/*
 * ─── R-1 (Daniel 2026-08-10): pipeline money + weekly lead funnel ─────
 *
 * Three components, one shared query key, so TanStack dedupes them into a
 * single request the same way KeyDatesStrip shares ['deal-deadlines'] with
 * DealDeadlinesCard.
 *
 * They are separate components rather than hooks inside Home() on purpose.
 * Home() early-returns <PartnerHome /> for portal users before its own
 * hooks run, so anything added to that body inherits a hook-order hazard
 * (React error #310). A child component with its own useQuery has none of
 * that, and it matches how ForecastStatCard and KeyDatesStrip already work.
 */
const PIPELINE_REPORT_KEY = ['home', 'pipeline-report'];

function usePipelineReport() {
  return useQuery({
    queryKey: PIPELINE_REPORT_KEY,
    queryFn: getPipelineReport,
    // Same 90s staleness as the forecast card. These numbers move when a
    // deal changes status, not second to second.
    staleTime: 90_000,
  });
}

/**
 * Pending volume + expected closings. Cards match the stat row above
 * exactly (same StatCard), on its own row so the counts and the dollars
 * do not compete for the same glance.
 */
function PipelineMoneyRow() {
  const { data } = usePipelineReport();
  if (!data) return null;

  const pending = data.pending_volume;
  const week = data.closings_forecast.this_week;
  const month = data.closings_forecast.this_month;
  const next = data.closings_forecast.next_month;
  /*
   * V-1 (Daniel 2026-08-10): these dollars are the team's for leadership and
   * the caller's own deals for everyone else. The server decides; this row
   * says which, in the hint text under each number.
   *
   * Saying it matters more than it looks. An agent who knew this card as the
   * team's pending volume and now sees a smaller figure under the same label
   * concludes the dashboard is broken and stops trusting the rest of it.
   */
  const ownScope = data.money_scope === 'own';
  const scopeNote = ownScope ? 'your deals' : 'team';

  // Nothing under contract and nothing scheduled means a brand-new team or
  // a genuinely empty pipeline. Either way an all-zero row is noise.
  if (pending.count === 0 && week.count === 0 && month.count === 0 && next.count === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <StatCard
          label="Pending volume"
          value={formatMoney(pending.volume_cents)}
          icon="briefcase"
          to="/transactions?status=under_contract"
          hint={`${pending.count} deal${pending.count === 1 ? '' : 's'} under contract · ${scopeNote}`}
          accent="warning"
        />
        <StatCard
          label="Closing this week"
          value={week.count}
          icon="calendar"
          to="/transactions"
          hint={`${formatMoney(week.volume_cents)} volume · ${scopeNote}`}
          accent="brand"
        />
        <StatCard
          label="Closing this month"
          value={month.count}
          icon="trending-up"
          to="/transactions"
          hint={`${formatMoney(month.volume_cents)} volume · ${scopeNote}`}
          accent="success"
        />
      </div>
      {/* Compact next-month line. A fourth card would make the row wrap on
          a laptop, and this number is a look-ahead rather than a headline. */}
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-muted)',
          marginTop: 10,
          paddingLeft: 2,
        }}
      >
        Next month: {next.count} expected closing{next.count === 1 ? '' : 's'}
        {' · '}
        {formatMoney(next.volume_cents)} volume
      </div>
    </div>
  );
}

/**
 * Weekly lead funnel. Leads in the last 7 days, how many came from ads,
 * and the appointments those leads produced.
 *
 * The appointment numbers are real (contact_activities.is_appointment,
 * migration 0177) but the flag was never backfilled, so a team that has
 * not started ticking it would read 0 appointments and 0% conversion.
 * That looks like a collapse rather than an unused checkbox, so when
 * nothing has ever been logged the card says so instead of showing a
 * percentage nobody should act on.
 */
function WeeklyLeadFunnelCard() {
  const { data } = usePipelineReport();
  if (!data) return null;
  const f = data.lead_funnel_week;
  // No leads at all in the window: nothing to report, and a row of zeros
  // on the dashboard is worse than no row.
  if (f.total_leads === 0) return null;

  const apptsTracked = f.appointments_tracked_since !== null;

  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-sm)',
        padding: 18,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ color: 'var(--text-muted)' }}>
          <Icon name="trending-up" size={15} />
        </span>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--text-heading)',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          Leads this week
        </h2>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          Last {f.days} days
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 16,
        }}
      >
        <FunnelFigure label="New leads" value={String(f.total_leads)} />
        <FunnelFigure
          label="From ads"
          value={String(f.from_ads)}
          sub={f.from_ads > 0 ? 'Meta lead forms' : undefined}
        />
        <FunnelFigure
          label="Appointments set"
          value={apptsTracked ? String(f.appointments_set) : '—'}
          sub={apptsTracked && f.from_ads_appointments_set > 0
            ? `${f.from_ads_appointments_set} from ads`
            : undefined}
        />
        <FunnelFigure
          label="Appointments held"
          value={apptsTracked ? String(f.appointments_held) : '—'}
        />
        <FunnelFigure
          label="Lead to appointment"
          value={apptsTracked ? `${f.conversion_rate}%` : '—'}
        />
      </div>

      {!apptsTracked && (
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '14px 0 0' }}>
          Appointment numbers start once appointments are logged on a lead or
          contact. Nothing has been marked as an appointment yet, so the
          conversion rate is left blank rather than shown as zero.
        </p>
      )}
    </section>
  );
}

function FunnelFigure({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 24,
          fontWeight: 700,
          color: 'var(--text-heading)',
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

/**
 * Pending volume per agent. Owner and operations only, the same pair of
 * roles that gates /finances in Layout.tsx, because dollars next to a
 * person's name is a leadership number.
 *
 * The role check here is a second lock, not the lock: the server sends an
 * empty by_agent to everyone else, so a UI-only check could not leak it.
 */
function PendingByAgentCard() {
  const { user } = useAuth();
  const { data } = usePipelineReport();
  const allowed = user?.roles?.some((r) => r === 'owner' || r === 'operations') ?? false;
  if (!allowed || !data) return null;
  const rows = data.pending_volume.by_agent;
  if (!data.pending_volume.by_agent_visible || rows.length === 0) return null;

  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
        marginBottom: 16,
      }}
    >
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--text-heading)',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          Pending volume by agent
        </h2>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>
          Deals under contract, counted against the primary agent
        </p>
      </div>
      {rows.map((r) => (
        <div
          key={r.user_id ?? r.name}
          style={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 70px 1fr',
            gap: 10,
            alignItems: 'center',
            padding: '10px 18px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Avatar name={r.name} size={28} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-heading)' }}>
              {r.name}
            </span>
          </div>
          <span
            style={{
              fontSize: 13,
              color: 'var(--text-muted)',
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {r.count}
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-heading)',
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatMoney(r.volume_cents)}
          </span>
        </div>
      ))}
    </section>
  );
}

function StatCard({
  label,
  value,
  icon,
  to,
  hint,
  accent = 'brand',
}: {
  label: string;
  value: number | string;
  icon: IconName;
  to: string;
  hint?: string;
  accent?: keyof typeof ACCENTS;
}) {
  const a = ACCENTS[accent];
  return (
    <Link
      to={to}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 18,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'transform 120ms, box-shadow 120ms, border-color 120ms',
        boxShadow: 'var(--shadow-sm)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow)';
        e.currentTarget.style.borderColor = 'var(--border-strong)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: a.bg,
            color: a.fg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={icon} size={16} />
        </div>
        <Icon name="arrow-up-right" size={14} style={{ color: 'var(--text-muted)' }} />
      </div>
      <div>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 30,
            fontWeight: 700,
            color: 'var(--text-heading)',
            letterSpacing: '-0.025em',
            lineHeight: 1,
          }}
        >
          {value}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-heading)', marginTop: 6 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{hint}</div>}
      </div>
    </Link>
  );
}

function RecentLeadsCard({ leads }: { leads: import('../api/leads').LeadListItem[] }) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-sm)',
        padding: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--text-muted)' }}>
            <Icon name="users" size={15} />
          </span>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--text-heading)',
              margin: 0,
              letterSpacing: '-0.01em',
            }}
          >
            Recent leads
          </h2>
        </div>
        <Link
          to="/leads"
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--brand-primary)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          View all
          <Icon name="chevron-right" size={12} />
        </Link>
      </div>
      {leads.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 12px', color: 'var(--text-muted)' }}>
          <div
            style={{
              display: 'inline-flex',
              padding: 12,
              borderRadius: '50%',
              background: 'var(--bg-surface-hover)',
              marginBottom: 10,
            }}
          >
            <Icon name="users" size={20} />
          </div>
          <div style={{ fontSize: 13 }}>
            No leads yet.{' '}
            <Link to="/leads/new" style={{ color: 'var(--brand-primary)', fontWeight: 500, textDecoration: 'none' }}>
              Add your first lead →
            </Link>
          </div>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {leads.slice(0, 6).map((l) => {
            const name = [l.first_name, l.last_name].filter(Boolean).join(' ') || '(no name)';
            return (
              <li key={l.id}>
                <Link
                  to={`/leads/${l.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: '1px solid var(--border)',
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'background 120ms',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar name={name} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-heading)' }}>{name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {l.source ?? '—'}
                        {l.lead_type ? ` · ${typeLabel(l.lead_type)}` : ''}
                      </div>
                    </div>
                  </div>
                  <StagePill stage={l.stage} />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * Recent client activity — buyers, sellers, and lenders who've logged in or
 * viewed their deal in the last 14 days. Rows highlight a "call now" prompt
 * when activity is within the last 24 hours.
 */
function ClientActivityCard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['client-activity'],
    queryFn: () => getClientActivity(14),
    staleTime: 60_000,
  });

  if (isLoading || isError) return null;
  const items = data?.items ?? [];
  // Hide entirely when no recent activity — saves the slot for something
  // more useful.
  if (items.length === 0) return null;

  const now = Math.floor(Date.now() / 1000);

  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-sm)',
        padding: 18,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ color: 'var(--text-muted)' }}>
          <Icon name="users" size={15} />
        </span>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--text-heading)',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          Recent client activity
        </h2>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          Last 14 days
        </span>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item, i) => (
          <ActivityRow key={item.user_id} item={item} isFirst={i === 0} now={now} />
        ))}
      </ul>
    </section>
  );
}

function ActivityRow({
  item,
  isFirst,
  now,
}: {
  item: ClientActivityItem;
  isFirst: boolean;
  now: number;
}) {
  const lastActivity = Math.max(item.last_view_at ?? 0, item.last_login_at ?? 0);
  const hoursAgo = Math.floor((now - lastActivity) / 3600);
  // "Hot" = activity within the last 24 hours. Show a call-prompt chip.
  const isHot = hoursAgo < 24 && lastActivity > 0;
  const name = `${item.first_name} ${item.last_name}`.trim();
  const portalLabel = item.portal === 'partner' ? 'Lender' : 'Client';

  const lastAddr = [
    item.last_view_address_street,
    item.last_view_address_city,
    item.last_view_address_state,
  ]
    .filter(Boolean)
    .join(', ');

  const linkTarget = item.last_view_transaction_id
    ? `/transactions/${item.last_view_transaction_id}`
    : '/transactions';

  return (
    <li>
      <Link
        to={linkTarget}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '10px 0',
          borderTop: isFirst ? 'none' : '1px solid var(--border)',
          textDecoration: 'none',
          color: 'inherit',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-surface-hover)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
          <Avatar name={name || item.email} />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-heading)' }}>
                {name || item.email}
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {portalLabel}
              </span>
              {isHot && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#D97706',
                    background: '#FEF3C7',
                    padding: '1px 7px',
                    borderRadius: 999,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  📞 Call them
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {lastActivity > 0 ? `Seen ${formatRelative(lastActivity)}` : 'Never seen'}
              {item.views_last_7d && item.views_last_7d > 0
                ? ` · ${item.views_last_7d} view${item.views_last_7d === 1 ? '' : 's'} last 7d`
                : ''}
              {lastAddr ? ` · ${lastAddr}` : ''}
            </div>
          </div>
        </div>
        <Icon name="chevron-right" size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      </Link>
    </li>
  );
}

function StagePill({ stage }: { stage: string }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    new: { bg: '#EFEDFB', fg: '#5B55B3' },
    nurturing: { bg: '#DDEBED', fg: '#205A60' },
    active: { bg: '#DCF1E5', fg: '#196B47' },
    under_contract: { bg: '#FBEACB', fg: '#7B5A1E' },
    closed: { bg: '#DDF4EA', fg: '#1F6C4C' },
    lost: { bg: '#EDEDF0', fg: '#5D5B6B' },
  };
  const c = colors[stage] ?? colors.new;
  return (
    <span
      style={{
        padding: '3px 8px',
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      {stage.replace('_', ' ')}
    </span>
  );
}

function typeLabel(t: 'auction' | 'listing' | 'buyer'): string {
  if (t === 'listing') return 'Seller';
  if (t === 'buyer') return 'Buyer';
  return 'Auction';
}

// =====================================================================
// PartnerHome — read-only dashboard for lender portal users.
// Shows just the transactions they're attached to as a party.
// =====================================================================

function PartnerHome() {
  const { user } = useAuth();
  const isLender = user?.portal === 'partner';
  const isClient = user?.portal === 'client';
  // CSV download failure — was a window.alert on a client/lender-facing page.
  const [csvError, setCsvError] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['transactions', user?.portal],
    queryFn: () => listTransactions(),
  });
  const { data: stats } = useQuery({
    queryKey: ['portal-stats', user?.portal],
    // Home page leaves the period as the server default (all_time) — the
    // Transactions list page is where the period selector lives.
    queryFn: () => getPortalStats(),
  });

  const txs = data?.transactions ?? [];

  // Clients never see a dashboard — the stats / lifetime production framing
  // doesn't make sense for buyers and sellers, and it's just one more page
  // between them and what they actually care about (their deal).
  //   - 1 deal  → straight to the transaction detail
  //   - 2+ deals → straight to the transactions list page
  //   - 0 deals → still hit the list page, which shows an empty state
  if (isClient && !isLoading) {
    if (txs.length === 1) return <Navigate to={`/transactions/${txs[0].id}`} replace />;
    return <Navigate to="/transactions" replace />;
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <Layout maxWidth={1080}>
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ minWidth: 0, flex: '1 1 260px' }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>{today}</div>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(24px, 6vw, 32px)',
              fontWeight: 700,
              color: 'var(--text-heading)',
              margin: 0,
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
            }}
          >
            {greeting}, {user?.first_name ?? 'there'}.
          </h1>
          <div style={{ marginTop: 8, fontSize: 14, color: 'var(--text-muted)', maxWidth: 600 }}>
            {isLender
              ? `You're viewing TurnKey's lender portal. Below are the active transactions where you're listed as the lender. Click any deal to see contract dates, contacts, and current status.`
              : `Welcome to TurnKey's client portal. Below are the transactions you're a party to. Click any deal to see contract dates, key contacts, and current status.`}
          </div>
        </div>
        {(txs.length > 0) && (
          <button
            type="button"
            onClick={() => downloadTransactionsCsv().catch((e) => setCsvError((e as Error).message))}
            style={{ ...ModernButton.secondary, flexShrink: 0 }}
          >
            <Icon name="file-text" size={14} />
            Download my deals
          </button>
        )}
      </div>

      {csvError && (
        <ResultSheet
          title="Download didn't work"
          lines={[csvError]}
          tone="danger"
          onClose={() => setCsvError(null)}
        />
      )}

      {/* auto-fit keeps three equal columns on desktop and collapses to
          one or two on a phone without any JS. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 28 }}>
        <PartnerStat
          label="In pipeline"
          primary={formatMoney(stats?.pipeline.volume_cents ?? 0)}
          secondary={`${stats?.pipeline.deals ?? 0} open deal${stats?.pipeline.deals === 1 ? '' : 's'}`}
        />
        <PartnerStat
          label="Closings this month"
          primary={String(stats?.closings_this_month.deals ?? 0)}
          secondary={
            (stats?.closings_this_month.deals ?? 0) > 0
              ? formatMoney(stats?.closings_this_month.volume_cents ?? 0) + ' volume'
              : 'None scheduled'
          }
        />
        <PartnerStat
          label="Lifetime production"
          primary={formatMoney(stats?.lifetime.volume_cents ?? 0)}
          secondary={`${stats?.lifetime.deals ?? 0} closed deal${stats?.lifetime.deals === 1 ? '' : 's'}`}
        />
      </div>

      <section
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        <header style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 16,
              fontWeight: 600,
              margin: 0,
              color: 'var(--text-heading)',
            }}
          >
            Your transactions
          </h2>
        </header>
        {isLoading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
        ) : txs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            {isLender
              ? 'No transactions assigned to you yet. The team will attach you to a deal when one comes in.'
              : 'No transactions yet. Your agent will attach you to a deal soon.'}
          </div>
        ) : (
          <div>
            {txs.map((t) => {
              const c = statusColor(t.status);
              return (
                <Link
                  key={t.id}
                  to={`/transactions/${t.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 8,
                    padding: '14px 20px',
                    borderBottom: '1px solid var(--border)',
                    color: 'var(--text-heading)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ minWidth: 0, flex: '1 1 auto', overflowWrap: 'anywhere' }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{formatAddress(t)}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {typeLabel(t.type)}
                      {t.phase_name ? ` • ${t.phase_name}` : ''}
                      {t.close_date ? ` • Close ${new Date(t.close_date * 1000).toLocaleDateString('en-US')}` : ''}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: c.bg,
                      color: c.fg,
                    }}
                  >
                    {statusLabel(t.status)}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </Layout>
  );
}

function PartnerStat({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary?: string;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '18px 20px',
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 26,
          fontWeight: 700,
          color: 'var(--text-heading)',
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
        }}
      >
        {primary}
      </div>
      {secondary && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{secondary}</div>
      )}
    </div>
  );
}

// ─── Approval queue card ─────────────────────────────────────────────
// Marketing posts submitted for the caller's approval, across every
// active deal on the team. The backend already filters to what this
// user is allowed to approve (auctions: ops only; listings/buyer:
// primary agent + ops), so we just render whatever comes back.
//
// Empty state hides the card entirely so users with nothing to
// approve don't see noise on the Home page.
function ApprovalQueueCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['team', 'approval-queue'],
    queryFn: getApprovalQueue,
    refetchInterval: 60_000, // refresh every minute so new submissions show up
  });
  const items = data?.items ?? [];
  // Hide entirely on initial load + when there's nothing to approve.
  if (isLoading || items.length === 0) return null;

  const stale = items.filter((i) => i.stale).length;

  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '20px 24px',
        marginBottom: 24,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>
            Awaiting your approval ({items.length})
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Marketing posts your team submitted for review.
            {stale > 0 && (
              <span style={{ color: 'var(--warning)', fontWeight: 500 }}>
                {' '}· {stale} have been waiting more than a week
              </span>
            )}
          </p>
        </div>
      </header>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item) => (
          <ApprovalQueueRow key={`${item.transaction_id}-${item.post_id}`} item={item} onActed={() => qc.invalidateQueries({ queryKey: ['team', 'approval-queue'] })} />
        ))}
      </div>
    </section>
  );
}

/**
 * Tiny thumbnail next to the post title on the approval queue. Pulls the
 * attached bytes via the authed endpoint and renders them inline so the
 * approver can scan the visual without leaving Home. Round L-16.
 */
function ApprovalQueueAttachmentThumb({
  transactionId,
  postId,
  mime,
}: {
  transactionId: number;
  postId: string;
  mime: string | null;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const isVideo = (mime ?? '').startsWith('video/');
  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(
          `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/transactions/${transactionId}/marketing-posts/${postId}/attachment`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
        );
        if (!res.ok) return;
        const blob = await res.blob();
        createdUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(createdUrl);
      } catch { /* swallow — fall back to the icon-only state */ }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [transactionId, postId]);

  return (
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 6,
        background: 'var(--bg-surface)',
        overflow: 'hidden',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--border)',
      }}
    >
      {url ? (
        isVideo ? (
          // Inline video preview — muted + loop so it acts as a quick
          // "show me the gist" without controls in the tiny tile.
          <video src={url} muted autoPlay loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img src={url} alt="post attachment" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )
      ) : (
        <span style={{ fontSize: 18, opacity: 0.5 }}>{isVideo ? '🎥' : '🖼️'}</span>
      )}
    </div>
  );
}

function ApprovalQueueRow({ item, onActed }: { item: ApprovalQueueItem; onActed: () => void }) {
  // Local optimistic state: hide the row right after the approve fires
  // so the user gets instant feedback. The refetch confirms shortly.
  const [hiddenLocally, setHiddenLocally] = useState(false);
  const approve = useMutation({
    mutationFn: () =>
      transitionMarketingPost(item.transaction_id, item.post_id, 'approved'),
    onSuccess: () => {
      setHiddenLocally(true);
      onActed();
    },
    // Was a window.alert. A failed approve usually means the post moved out
    // from under you, so the row stays put and says why inline.
  });
  if (hiddenLocally) return null;

  const submitter =
    [item.submitted_by_first, item.submitted_by_last].filter(Boolean).join(' ') || 'A teammate';
  const submittedRel = item.submitted_at ? formatRelative(item.submitted_at) : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        background: item.stale ? 'var(--warning-soft)' : 'var(--bg-surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        flexWrap: 'wrap',
      }}
    >
      {/* Round L-16 (Jesus / Daniel 2026-06-10): show the attached
          graphic/video right in the approval queue so the approver
          doesn't have to click through to see the visual. */}
      {item.has_attachment && (
        <ApprovalQueueAttachmentThumb
          transactionId={item.transaction_id}
          postId={item.post_id}
          mime={item.attachment_mime}
        />
      )}
      <Link
        to={`/transactions/${item.transaction_id}?t=marketing`}
        style={{
          flex: 1,
          minWidth: 220,
          textDecoration: 'none',
          color: 'var(--text-heading)',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 500 }}>
          {item.post_title}
          {item.has_attachment && (
            <span
              title={`${item.attachment_mime?.startsWith('video/') ? 'Video' : 'Image'} attached: ${item.attachment_name ?? ''}`}
              style={{ marginLeft: 8, fontSize: 11 }}
            >
              {item.attachment_mime?.startsWith('video/') ? '🎥' : '🖼️'}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {item.address} · {item.transaction_type}
        </div>
      </Link>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 130, textAlign: 'right' }}>
        {submitter}
        <br />
        {submittedRel}
      </div>
      <button
        onClick={() => approve.mutate()}
        disabled={approve.isPending}
        style={{
          background: 'var(--brand-primary)',
          color: 'white',
          border: 'none',
          padding: '6px 14px',
          borderRadius: 'var(--radius)',
          fontSize: 12,
          fontWeight: 600,
          cursor: approve.isPending ? 'wait' : 'pointer',
          opacity: approve.isPending ? 0.7 : 1,
        }}
      >
        {approve.isPending ? 'Approving…' : 'Approve'}
      </button>
      {approve.isError && (
        <div role="alert" style={{ flexBasis: '100%', fontSize: 12, color: 'var(--danger)' }}>
          Couldn't approve: {(approve.error as Error).message}
        </div>
      )}
    </div>
  );
}
